import { RumInitConfiguration } from '@datadog/browser-rum';
import { PageNavigationEventContextType } from '@repo/platform';
import { InitOption } from 'airbridge-web-sdk-loader/build/airbridge_api';
import { Config } from 'mixpanel-browser';
import { ReactNode } from 'react';

export type SendTarget = 'mixpanel' | 'airbridge' | 'tiktokPixel' | 'googleAnalytics' | 'datadog';

export type SendEventOptions = {
  isAllowRetry?: boolean;
  sendTargets?: readonly SendTarget[];
};

export type SendEventReturn = { config: Record<string, unknown>; event: Record<string, unknown> } | void;

export type EventTrackingContextType = {
  eventUser: SetUserParams | null | undefined;
  mixpanelDeviceId: string;
  pageName: string;
  setPageName: (pageName: string) => void;
  setUser: (params: SetUserParams) => void;
  unsetUser: () => void;
  sendEvent: (eventName: string, properties?: Record<string, unknown>, options?: SendEventOptions) => void;
  sendViewEvent: (eventName: string, properties?: Record<string, unknown>, options?: SendEventOptions) => void;
  sendClickEvent: (eventName: string, properties?: Record<string, unknown>, options?: SendEventOptions) => void;
  sendScrollEvent: (eventName: string, properties?: Record<string, unknown>, options?: SendEventOptions) => void;
  sendRequestEvent: (eventName: string, properties?: Record<string, unknown>, options?: SendEventOptions) => void;
  sendMetaEvent: (eventName: MetaPixelEventNames, params?: MetaPixelParams) => void;
  sendKakaoEvent: (methodName: KakaoPixelMethods, params?: KakaoPixelEventParams) => void;
  sendNaverEvent: (params: NaverPixelEventParams) => void;
  sendDatadogLog: (data: DatadogEventData) => void;
};

export type EventTrackingInitializeProps = { appId: string; clientId: string; landingId?: string };
export type MixpanelInitializeProps = { canInitializeMixpanel?: boolean; mixpanelKey?: string; mixpanelConfig?: Partial<Config>; enableLoginStatusProperty?: boolean };
export type AirbridgeInitializeProps = {
  airbridgeAppId?: string;
  airbridgeWebToken?: string;
  airbridgeConfig?: Partial<InitOption>;
};
export type MetaPixelInitializeProps = { metaPixelId?: string };
export type TiktokPixelInitializeProps = { tiktokPixelId?: string };
export type NaverPixelInitializeProps = { naverPixelId?: string };
export type KakaoPixelInitializeProps = { kakaoPixelId?: string };
export type GoogleTrackingInitializeProps = { googleTrackingIds?: string[]; initIds?: string[] };
export type DatadogInitializeProps = { datadogConfig?: RumInitConfiguration; appVersion?: string };
export type EventTrackingControllerProps = EventTrackingInitializeProps &
  AirbridgeInitializeProps &
  MixpanelInitializeProps &
  MetaPixelInitializeProps &
  TiktokPixelInitializeProps &
  NaverPixelInitializeProps &
  GoogleTrackingInitializeProps &
  KakaoPixelInitializeProps &
  DatadogInitializeProps;

export type SendEventProperties = {
  [k: string]: string | number | unknown;
};

export type SendEventParams = {
  eventName: string;
  properties?: SendEventProperties;
};

export type SetUserParams = {
  id: string;
  name?: string;
  phone?: string;
  email?: string;
  accessType?: string;
};

export type MetaPixelUserData = {
  external_id: string;
  em: string;
  ph: string;
};

export type NaverPixelItems = {
  id: string;
  name: string;
  quantity: number;
  category?: string;
  payAmount: number;
  option?: string;
};

export type NaverPixelEventParams = {
  id?: string;
  type:
    | 'purchase'
    | 'begin_checkout'
    | 'sign_up'
    | 'add_to_cart'
    | 'schedule'
    | 'save_store'
    | 'add_to_wishlist'
    | 'inquiry'
    | 'call'
    | 'lead'
    | 'view_product'
    | 'view_content'
    | 'search'
    | 'share'
    | 'clip_coupon'
    | 'view_item_list'
    | 'about_us'
    | 'staff'
    | 'location'
    | 'promotion'
    | 'add_contact_method'
    | 'opt_in_marketing'
    | 'subscribe'
    | 'review'
    | 'custom_001'
    | 'custom_002'
    | 'custom_003'
    | 'custom_004'
    | 'custom_005'
    | 'custom_006'
    | 'custom_007'
    | 'custom_008'
    | 'custom_009'
    | 'custom_010';
  value?: string;
  currency?: string;
  items?: NaverPixelItems[];
};

export type EventTrackingProviderProps = {
  children: ReactNode;
  appId: string;
  clientId: string;
  landingId?: string;
  isUseAuthPackages?: boolean;
  mixpanelProps?: MixpanelInitializeProps;
  airbridgeProps?: AirbridgeInitializeProps;
  metaPixelProps?: MetaPixelInitializeProps;
  tiktokPixelProps?: TiktokPixelInitializeProps;
  naverPixelProps?: NaverPixelInitializeProps;
  kakaoPixelProps?: KakaoPixelInitializeProps;
  navigationEvent?: PageNavigationEventContextType;
  googleTrackingProps?: GoogleTrackingInitializeProps;
  datadogProps?: DatadogInitializeProps;
};

export const META_PIXEL_STANDARD_EVENTS = [
  'PageView',
  'ViewContent',
  'Search',
  'AddToCart',
  'AddToWishlist',
  'InitiateCheckout',
  'AddPaymentInfo',
  'Purchase',
  'Lead',
  'CompleteRegistration',
  'CustomizeProduct',
  'StartTrial',
  'SubmitApplication',
  'Subscribe',
  'Contact',
  'Donate',
  'FindLocation',
  'Schedule'
] as const;

export type MetaPixelStandardEventNames = (typeof META_PIXEL_STANDARD_EVENTS)[number];

export type MetaPixelEventNames = MetaPixelStandardEventNames | (string & {});

export type MetaPixelParams = {
  content_category?: string;
  content_ids?: Array<string | number>;
  content_name?: string;
  content_type?: string;
  currency?: string;
  value?: number;
  num_items?: number;
  search_string?: string;
  status?: boolean;
  predicted_ltv?: number;
  contents?: Array<{
    id: string | number;
    quantity: number;
    item_price?: number;
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
};

export type KakaoPixelMethods =
  | 'pageView'
  | 'completeRegistration'
  | 'login'
  | 'addToCart'
  | 'purchase'
  | 'viewContent'
  | 'viewCart'
  | 'addToWishList'
  | 'preparation'
  | 'tutorial'
  | 'missionComplete';

export type KakaoPixelEventParams =
  | {
      products: Array<{ id: string; name?: string; quantity: number; price: number; brand?: string }>;
      [key: string]: unknown;
    }
  | string;

export type DatadogEventData =
  | { type: 'error'; error: unknown; context?: object }
  | { type: 'timing'; name: string; time?: number }
  | { type: 'durationVital'; name: string; startTime?: number; duration: number; context?: object; description?: string }
  | { type: 'featureFlag'; name: string; value: any }
  | { type: 'logger'; message: string; messageContext?: object; error?: Error; level?: 'info' | 'debug' | 'error' | 'warn' }
  | { type: 'action'; name: string; context?: object };

export type DatadogEventProperties = SendEventProperties & {
  error?: Error;
  context?: object;
  name?: string;
  duration?: number;
  description?: string;
  value?: unknown;
  message?: string;
  messageContext?: object;
};

declare global {
  interface Window {
    fbq: (action: string, event: string, params?: MetaPixelParams, duplicationParams?: { eventID: string }) => void;
    FB: any;
    gtag: any;
    ttq?: {
      page: () => void;
      track: (eventName: string, params: any) => void;
      identify: ({ email, phone_number, external_id }: { email: string; phone_number: string; external_id: string }) => void;
      instance: (instance_id: string) => any;
      debug: any;
      on: any;
      off: any;
      once: any;
      ready: any;
      load: (pixelId: string) => void;
    };
    wcs: {
      inflow: (hostname: string) => void;
      trans: (event: NaverPixelEventParams) => void;
    };
    wcs_add: Record<string, string>;
    wcs_do: () => void;
    dataLayer?: object[];
    kakaoPixel: any;
  }
}
